import { BackRequirementMode, getBackRequirementMode } from '../back-requirement';
import evaluateFieldConditions from '../evaluate-field-conditions';
import { parseIdKey } from '../ids';
import {
  runDynamicDependentsForHiddenUpdates,
  runHiddenDependentsChained,
} from './dependent-field-processing';

const uniq = arr => Array.from(new Set(arr));
const sortAlpha = arr => [...arr].sort();
const isStr = v => typeof v === 'string' && v.length > 0;
const numOrNull = v => (Number.isFinite(v) ? v : null);
const splitIdValue = optionValue => parseIdKey(optionValue);

// evaluateFieldConditions treats "value doesn't exist" as condition-failed for every operator,
// including negation ones (is_not/not_in/not_contains) — logically backwards for those, since
// "the candidate hasn't answered this yet" is not the same as "answered in a way that makes
// this section not apply." For a brand-new candidate, that false negative made isActive()
// return false (list "invisible") for a list whose visibility is gated on an unanswered
// question, and setTrueIfInvisible then marked its requirements as already met with zero
// uploads. Only short-circuits negation operators on plain form-field codes (not
// company./candidate./etc. context-sourced ones, which aren't "unanswered" in this sense).
const NEGATION_OPS = new Set(['is_not', 'not_in', 'not_contains']);

export const hasUnansweredNegationDependency = (conditions, values) => {
  if (!conditions) return false;
  if (Array.isArray(conditions)) {
    return conditions.some(c => hasUnansweredNegationDependency(c, values));
  }
  if (conditions.op === 'and' || conditions.op === 'or') {
    return hasUnansweredNegationDependency(conditions.conditions, values);
  }
  const { op, code } = conditions;
  if (!op || !isStr(code) || code.includes('.')) return false;
  if (!NEGATION_OPS.has(op)) return false;
  // Loose check deliberately: a page-scoped form reset elsewhere in the pipeline can wipe a
  // field from another page's snapshot down to a literal `null` rather than leaving it
  // `undefined` — both mean "we don't actually know this answer right now", not "answered".
  return values[code] == null;
};

const isActive = (field, values = {}, fieldPathIndex = {}) => {
  try {
    if (!field?.conditions) return true;
    if (hasUnansweredNegationDependency(field.conditions, values)) return true;
    return !!evaluateFieldConditions(field.conditions, values || {}, { fieldPathIndex });
  } catch {
    return true;
  }
};

// Check if an upload is accepted (verified, needs_review, not_applicable, or failed with >= 3 attempts)
// Excludes rejected uploads and failed uploads with < 3 attempts
// For non-ID documents (no id_upload_detail), consider them accepted if they exist and are assigned to a list
export const isAcceptedUpload = upload => {
  if (!upload) return false;

  // If there's no id_upload_detail, it's a non-ID document
  // Only consider it accepted if it has id_requirements_list in metadata (assigned to a list)
  if (!upload?.id_upload_detail) {
    const listCode = upload?.metadata?.id_requirements_list ?? null;
    // Must have id_requirements_list in metadata to be considered accepted
    return isStr(listCode);
  }

  const status = upload?.id_upload_detail?.status;
  const attempts = upload?.metadata?.attempts || 0;
  return (
    status === 'verified' ||
    status === 'needs_review' ||
    status === 'not_applicable' ||
    (status === 'failed' && attempts >= 3)
  );
};

// An accepted upload whose matched option has extracted fields still needs the candidate to
// confirm them before it can count toward ANY list's requirement (its own or one it's
// pool-credited to) — otherwise a list can show "Complete" off a document the candidate
// hasn't actually reviewed yet. Options without extracted fields only need isAcceptedUpload
// (verified/needs_review/not_applicable/force-accepted after 3 attempts).
export const isUploadReadyToCount = (upload, opt) => {
  if (!isAcceptedUpload(upload)) return false;
  const displayFields = opt?.inputFields ?? opt?.input_fields ?? [];
  if (Array.isArray(displayFields) && displayFields.length > 0) {
    return !!upload?.id_upload_detail?.confirmed_by_cand;
  }
  return true;
};

// Check if an upload counts as verified for strict check-type requirements
// Includes verified status AND not_applicable (skip_verification documents count as passing)
export const isVerifiedUpload = upload => {
  if (!upload) return false;
  const status = upload?.id_upload_detail?.status;
  return status === 'verified' || status === 'not_applicable';
};

// Build { [idType]: [countryCode, ...] } and filter valid uploads
// Note: For dual uploads, we need to check if back_upload_id exists in id_upload_detail
// This check is done later in updateIdRequirementsGroup when we know which options are dual
export const computeSelectedIdsFromUploads = (idUploads = []) => {
  // Ensure idUploads is always an array (handles null being passed explicitly)
  const safeIdUploads = idUploads || [];

  const selectedIdTypes = {};
  const validUploads = [];
  let hasDobId = false;
  let hasPhotoId = false;
  // Verified and not_applicable only. needs_review stays in selectedIdTypes for the form,
  // but must not satisfy a check's ID requirements.
  const verifiedSelectedIdTypes = {};
  let verifiedHasDobId = false;
  let verifiedHasPhotoId = false;

  for (const upload of safeIdUploads) {
    const idValue = upload?.metadata?.id_type || null;
    const { idType, countryCode } = splitIdValue(idValue);
    if (!isStr(idType)) continue;

    if (!isAcceptedUpload(upload)) continue;

    // Add to valid uploads
    validUploads.push(upload);

    // Build selected types
    if (!selectedIdTypes[idType]) selectedIdTypes[idType] = [];
    if (isStr(countryCode)) selectedIdTypes[idType].push(countryCode);

    const uploadIsVerified = isVerifiedUpload(upload);
    if (uploadIsVerified) {
      if (!verifiedSelectedIdTypes[idType]) verifiedSelectedIdTypes[idType] = [];
      if (isStr(countryCode)) verifiedSelectedIdTypes[idType].push(countryCode);
    }

    // Check extracted fields for DOB and photo info from ID verification
    const extractedFields = upload?.id_upload_detail?.extracted_fields;
    if (extractedFields?.includes_dob === true) {
      hasDobId = true;
      if (uploadIsVerified) {
        verifiedHasDobId = true;
      }
    }

    if (upload?.id_upload_detail?.has_photo === true) {
      hasPhotoId = true;
      if (uploadIsVerified) {
        verifiedHasPhotoId = true;
      }
    }
  }

  for (const [idType, countries] of Object.entries(selectedIdTypes)) {
    selectedIdTypes[idType] = sortAlpha(uniq(countries));
  }
  for (const [idType, countries] of Object.entries(verifiedSelectedIdTypes)) {
    verifiedSelectedIdTypes[idType] = sortAlpha(uniq(countries));
  }

  return {
    selectedIdTypes,
    verifiedSelectedIdTypes,
    validUploads,
    hasDobId,
    hasPhotoId,
    verifiedHasDobId,
    verifiedHasPhotoId,
  };
};

const isSelectedId = (selectedIds, idType, countryCode = null) => {
  if (!isStr(idType)) return false;
  if (countryCode) {
    const arr = selectedIds[idType] || [];
    return arr.includes(countryCode);
  }
  // Generic presence = the key exists, regardless of array contents
  return Object.prototype.hasOwnProperty.call(selectedIds, idType);
};

const isMutuallyExclusiveOptSelected = (opt, selectedIds) => {
  const rules = Array.isArray(opt?.mutuallyExclusiveWith) ? opt.mutuallyExclusiveWith : [];

  if (rules.length === 0) return false;

  return rules.some(rule => {
    if (!isStr(rule)) return false;
    const { idType, countryCode } = splitIdValue(rule);
    return isSelectedId(selectedIds, idType, countryCode || null);
  });
};

const hasSpecificApplied = (appliedSet, idType) => {
  const prefix = `${idType}:`;
  for (const key of appliedSet) if (key.startsWith(prefix)) return true;
  return false;
};

// Check if a dual upload is complete (has both front and back)
const isDualUploadComplete = upload => {
  if (!upload) return false;
  // Check if this upload has a back_upload_id in its id_upload_detail
  const backUploadId = upload?.id_upload_detail?.back_upload_id;
  return backUploadId !== null && backUploadId !== undefined;
};

const isSupportAcceptedFailedUpload = upload => {
  if (!upload) return false;
  const status = upload?.id_upload_detail?.status;
  const attempts = upload?.metadata?.attempts || 0;
  return status === 'failed' && attempts >= 3;
};

// Always-dual options need a back. Conditional dual (backConditions) only needs a back when
// extracted fields satisfy those conditions — same rule DocumentDualUpload uses at verify time.
// Support-accepted (failed >= 3 attempts) uploads are treated as complete even without a back:
// the candidate was never asked for one under the option they uploaded against (e.g. NZ licence
// via driver_license:AU), and pool reassignment to another list's dual option must still work.
export const isDualOptionSatisfied = (upload, opt) => {
  if (opt?.dual !== true) return true;
  if (isSupportAcceptedFailedUpload(upload)) return true;

  const backConditions = opt.backConditions;
  if (getBackRequirementMode(backConditions) === BackRequirementMode.CONDITIONAL) {
    const extracted = upload?.id_upload_detail?.extracted_fields || {};
    const backRequired = evaluateFieldConditions(backConditions, extracted, {});
    if (!backRequired) return true;
  }

  return isDualUploadComplete(upload);
};

// Check if an expiry date meets the tolerance requirement
// Returns true if the expiry date is within tolerance (can be expired up to tolerance months in the past)
// Date format is DD/MM/YYYY as returned by Regula
// Example: if tolerance is 24 months, checks if expiry >= (today - 24 months)
// This means the ID can be expired by up to 24 months and still be accepted
const meetsExpiryTolerance = (idExpiry, toleranceMonths) => {
  if (!idExpiry || toleranceMonths == null) return true;

  try {
    const [day, month, year] = idExpiry.split('/').map(Number);
    if (!day || !month || !year) return true;

    // JS Date months are 0-indexed (0=Jan, 11=Dec), so subtract 1
    const expiryDate = new Date(year, month - 1, day);
    if (isNaN(expiryDate.getTime())) return true;

    const now = new Date();
    // Calculate minimum valid expiry: today - tolerance months (allows expired IDs up to tolerance months old)
    const minExpiryDate = new Date(
      now.getFullYear(),
      now.getMonth() - toleranceMonths,
      now.getDate(),
    );
    return expiryDate >= minExpiryDate;
  } catch {
    return true;
  }
};

// Check if an upload matches an option and is complete (for dual options, checks back_upload_id)
// Also checks expiry tolerance if the option requires it and the ID is from a different list
const uploadMatchesOption = (upload, opt, idType, countryCode, listCode = null) => {
  if (!upload) return false;
  const uploadIdValue = upload?.metadata?.id_type || null;
  const { idType: uploadIdType, countryCode: uploadCountryCode } = splitIdValue(uploadIdValue);

  // Check if upload matches the option's idType and countryCode
  const matchesType = uploadIdType === idType;
  const matchesCountry = countryCode ? uploadCountryCode === countryCode : true;

  if (!matchesType || !matchesCountry) return false;

  // If option has expiry tolerance requirement, check the id_expiry date
  // Only check if the ID is from a different list (cross-list reuse)
  // If the ID is from the same list, it would have already been validated during verification
  if (opt?.expiryTolerance !== null && opt?.expiryTolerance !== undefined && isStr(listCode)) {
    // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
    const uploadListCode =
      upload?.id_upload_detail?.id_requirements_list ??
      upload?.metadata?.id_requirements_list ??
      null;

    // Only check expiry tolerance if the upload is from a different list.
    // Support-accepted (failed >= 3 attempts) uploads skip expiry so they can still
    // count toward non-verified allocation — but must still pass dual completeness below.
    if (uploadListCode !== listCode && !isSupportAcceptedFailedUpload(upload)) {
      const idExpiry = upload?.id_upload_detail?.id_expiry;
      const toleranceMonths =
        typeof opt.expiryTolerance === 'number'
          ? opt.expiryTolerance
          : parseInt(opt.expiryTolerance, 10);

      if (!isNaN(toleranceMonths) && toleranceMonths >= 0) {
        if (!meetsExpiryTolerance(idExpiry, toleranceMonths)) {
          return false;
        }
      }
    }
  }

  return isDualOptionSatisfied(upload, opt);
};

const getRawListCode = upload =>
  upload?.id_upload_detail?.id_requirements_list ?? upload?.metadata?.id_requirements_list ?? null;

const minForAssignmentList = list => {
  const n = numOrNull(list?.min ?? null);
  if (n == null) return 0;
  return Math.max(0, n);
};

const detectDuplicateIdValuesFromLists = lists => {
  const valueToListCodes = new Map();
  const valueHasAllowCrossList = new Map();

  for (const list of lists) {
    const listCode = list.code;
    if (!isStr(listCode)) continue;

    const seenInList = new Set();
    for (const opt of list.options || []) {
      const value = opt.value ?? null;
      if (!isStr(value) || seenInList.has(value)) continue;
      seenInList.add(value);

      if (!valueToListCodes.has(value)) {
        valueToListCodes.set(value, new Set());
        valueHasAllowCrossList.set(value, false);
      }
      valueToListCodes.get(value).add(listCode);
      if (opt.allowCrossListCount) {
        valueHasAllowCrossList.set(value, true);
      }
    }
  }

  const duplicates = [];
  for (const [value, listCodes] of valueToListCodes) {
    if (listCodes.size >= 2 && !valueHasAllowCrossList.get(value)) {
      duplicates.push(value);
    }
  }
  return duplicates;
};

const findMatchingOptionForUpload = (list, listCode, upload) => {
  const uploadIdValue = upload?.metadata?.id_type;
  if (!isStr(uploadIdValue)) return null;

  const { idType, countryCode } = splitIdValue(uploadIdValue);

  for (const opt of list.options || []) {
    const optIdType = opt.idType ?? null;
    const optCountryCode = opt.countryCode ?? null;
    if (!isStr(optIdType)) continue;

    if (opt.value === uploadIdValue) {
      const optCountry = isStr(optCountryCode) ? optCountryCode : null;
      if (uploadMatchesOption(upload, opt, optIdType, optCountry, listCode)) {
        return { opt, matchKind: isStr(optCountryCode) ? 'specific' : 'generic' };
      }
    }
  }

  if (isStr(countryCode)) {
    for (const opt of list.options || []) {
      const optIdType = opt.idType ?? null;
      const optCountryCode = opt.countryCode ?? null;
      if (!isStr(optIdType) || isStr(optCountryCode) || optIdType !== idType) continue;
      if (uploadMatchesOption(upload, opt, optIdType, null, listCode)) {
        return { opt, matchKind: 'generic' };
      }
    }
  }

  return null;
};

const matchKindRank = matchKind => (matchKind === 'specific' ? 2 : 1);

const optionContributionForPool = opt => ({
  count: Number.isFinite(opt?.countValue) ? opt.countValue : 1,
  points: Number.isFinite(opt?.points) ? opt.points : 1,
});

const effectiveListCodeForPoolUpload = (upload, assignmentOut, groupListCodes) => {
  const uid = upload?.id;
  if (uid != null && assignmentOut.has(uid)) {
    return assignmentOut.get(uid);
  }
  const raw = getRawListCode(upload);
  if (isStr(raw) && groupListCodes.has(raw)) return raw;
  return null;
};

const computeListTotalsForPool = (
  list,
  listCode,
  assignmentOut,
  uploads,
  excludeUploadId,
  groupListCodes,
) => {
  let count = 0;
  let points = 0;

  for (const upload of uploads) {
    if (!isAcceptedUpload(upload) || upload?.id === excludeUploadId) continue;
    const eff = effectiveListCodeForPoolUpload(upload, assignmentOut, groupListCodes);
    if (eff !== listCode) continue;
    const match = findMatchingOptionForUpload(list, listCode, upload);
    if (!match) continue;
    const { count: cv, points: pv } = optionContributionForPool(match.opt);
    count += cv;
    points += pv;
  }

  return { count, points };
};

const listNeedsMoreForPool = (
  list,
  listCode,
  assignmentOut,
  uploads,
  excludeUploadId,
  groupListCodes,
) => {
  const { count, points } = computeListTotalsForPool(
    list,
    listCode,
    assignmentOut,
    uploads,
    excludeUploadId,
    groupListCodes,
  );
  const needCount = numOrNull(list?.min ?? null);
  const needPoints = numOrNull(list?.minPoints ?? null);
  const needsCount = needCount != null && count < needCount;
  const needsPoints = needPoints != null && points < needPoints;
  return needsCount || needsPoints;
};

const isRawListInGroup = (upload, groupListCodes) => {
  const raw = getRawListCode(upload);
  return isStr(raw) && groupListCodes.has(raw);
};

const canFillNativeOnlyForPool = (
  entry,
  assignmentOut,
  uploads,
  assignedUploadIds,
  groupListCodes,
) => {
  const sim = new Map(assignmentOut);
  for (const upload of uploads) {
    const uid = upload?.id;
    if (uid == null || assignedUploadIds.has(uid)) continue;
    if (!isAcceptedUpload(upload)) continue;
    if (getRawListCode(upload) !== entry.code) continue;
    if (!findMatchingOptionForUpload(entry.list, entry.code, upload)) continue;
    sim.set(uid, entry.code);
  }
  return !listNeedsMoreForPool(entry.list, entry.code, sim, uploads, null, groupListCodes);
};

const sourceListSurvivesMoveAway = (
  rawListCode,
  upload,
  assignmentOut,
  uploads,
  groupListCodes,
  entries,
) => {
  const rawEntry = entries.find(e => e.code === rawListCode);
  if (!rawEntry) return true;
  return !listNeedsMoreForPool(
    rawEntry.list,
    rawListCode,
    assignmentOut,
    uploads,
    upload?.id ?? null,
    groupListCodes,
  );
};

function computePoolEffectiveAssignments(eligibleLists, safeIdUploads) {
  const out = new Map();
  if (!eligibleLists.length) return out;

  const duplicateIdValuesSet = new Set(detectDuplicateIdValuesFromLists(eligibleLists));
  const groupListCodes = new Set(eligibleLists.map(list => list.code).filter(isStr));
  const entries = eligibleLists.map((list, orderIdx) => ({
    list,
    code: list.code,
    orderIdx,
    minCount: minForAssignmentList(list),
    fill: 0,
  }));

  const assignedUploadIds = new Set();
  const deficit = entry => Math.max(0, entry.minCount - entry.fill);

  const unassigned = () =>
    safeIdUploads.filter(
      upload => isAcceptedUpload(upload) && upload?.id != null && !assignedUploadIds.has(upload.id),
    );

  const buildCandidate = (entry, upload, match, phase) => {
    const uploadIdValue = upload?.metadata?.id_type;
    const isDuplicate = isStr(uploadIdValue) && duplicateIdValuesSet.has(uploadIdValue);
    const raw = getRawListCode(upload);
    return {
      entry,
      upload,
      deficit: phase === 'deficit' ? deficit(entry) : 0,
      matchKindRank: matchKindRank(match.matchKind),
      orderIdx: entry.orderIdx,
      prefersRaw: !isDuplicate && isStr(raw) && raw === entry.code ? 1 : 0,
      rawFallback: isDuplicate && isStr(raw) && raw === entry.code ? 1 : 0,
    };
  };

  const isBetterCandidate = (challenger, incumbent, phase) => {
    if (phase === 'deficit') {
      if (challenger.deficit !== incumbent.deficit) return challenger.deficit > incumbent.deficit;
      if (challenger.rawFallback !== incumbent.rawFallback) {
        return challenger.rawFallback > incumbent.rawFallback;
      }
      if (challenger.prefersRaw !== incumbent.prefersRaw) {
        return challenger.prefersRaw > incumbent.prefersRaw;
      }
      if (challenger.matchKindRank !== incumbent.matchKindRank) {
        return challenger.matchKindRank > incumbent.matchKindRank;
      }
      return challenger.orderIdx < incumbent.orderIdx;
    }
    if (challenger.rawFallback !== incumbent.rawFallback) {
      return challenger.rawFallback > incumbent.rawFallback;
    }
    if (challenger.prefersRaw !== incumbent.prefersRaw) {
      return challenger.prefersRaw > incumbent.prefersRaw;
    }
    if (challenger.matchKindRank !== incumbent.matchKindRank) {
      return challenger.matchKindRank > incumbent.matchKindRank;
    }
    return challenger.orderIdx < incumbent.orderIdx;
  };

  const pickBest = (candidates, phase) => {
    if (!candidates.length) return null;
    return candidates.reduce((best, candidate) =>
      !best || isBetterCandidate(candidate, best, phase) ? candidate : best,
    );
  };

  const commitAssignment = best => {
    out.set(best.upload.id, best.entry.code);
    assignedUploadIds.add(best.upload.id);
    best.entry.fill += 1;
  };

  const collectCandidates = (filterEntry, filterUpload, phase) => {
    const candidates = [];
    for (const upload of unassigned()) {
      if (filterUpload && !filterUpload(upload)) continue;
      for (const entry of entries) {
        if (filterEntry && !filterEntry(entry, upload)) continue;
        const match = findMatchingOptionForUpload(entry.list, entry.code, upload);
        if (!match) continue;
        candidates.push(buildCandidate(entry, upload, match, phase));
      }
    }
    return candidates;
  };

  // In-group uploads: keep on raw list while it still needs min / minPoints.
  while (true) {
    const best = pickBest(
      collectCandidates(
        (entry, upload) => {
          if (getRawListCode(upload) !== entry.code) return false;
          return listNeedsMoreForPool(
            entry.list,
            entry.code,
            out,
            safeIdUploads,
            null,
            groupListCodes,
          );
        },
        upload => isRawListInGroup(upload, groupListCodes),
        'deficit',
      ),
      'deficit',
    );
    if (!best) break;
    commitAssignment(best);
  }

  // In-group cross-list: only when target still needs more, cannot fill from native
  // uploads alone, and source list still meets min / minPoints without this upload.
  while (true) {
    const candidates = [];
    for (const upload of unassigned()) {
      if (!isRawListInGroup(upload, groupListCodes)) continue;
      const raw = getRawListCode(upload);
      for (const entry of entries) {
        if (entry.code === raw) continue;
        if (
          !listNeedsMoreForPool(entry.list, entry.code, out, safeIdUploads, null, groupListCodes)
        ) {
          continue;
        }
        if (
          canFillNativeOnlyForPool(entry, out, safeIdUploads, assignedUploadIds, groupListCodes)
        ) {
          continue;
        }
        if (!sourceListSurvivesMoveAway(raw, upload, out, safeIdUploads, groupListCodes, entries)) {
          continue;
        }
        const match = findMatchingOptionForUpload(entry.list, entry.code, upload);
        if (!match) continue;
        candidates.push(buildCandidate(entry, upload, match, 'deficit'));
      }
    }
    const best = pickBest(candidates, 'deficit');
    if (!best) break;
    commitAssignment(best);
  }

  // Outside-group uploads: assign to the best matching list (no raw-list protection).
  while (true) {
    const best = pickBest(
      collectCandidates(null, upload => !isRawListInGroup(upload, groupListCodes), 'remainder'),
      'remainder',
    );
    if (!best) break;
    commitAssignment(best);
  }

  // Remaining in-group uploads: prefer raw list, then best match.
  while (unassigned().length > 0) {
    const best = pickBest(
      collectCandidates(null, upload => isRawListInGroup(upload, groupListCodes), 'remainder'),
      'remainder',
    );
    if (best) {
      commitAssignment(best);
      continue;
    }

    const upload = unassigned()[0];
    const raw = getRawListCode(upload);
    if (isStr(raw)) {
      const entry = entries.find(e => e.code === raw);
      if (entry) {
        out.set(upload.id, raw);
        assignedUploadIds.add(upload.id);
        entry.fill += 1;
        continue;
      }
    }
    break;
  }

  return out;
}

const updateIdRequirementsGroup = (
  wrapperField,
  selectedIds,
  values = {},
  fieldPathIndex = {},
  idUploads = [],
  verifiedValues = {}, // Values with hidden fields computed from verified-only uploads
  globalActiveListCodes = null, // Active list codes across EVERY id_requirements group (all check
  // types), not just this one — see activeListCodes below for why this must span groups.
) => {
  // Ensure idUploads is always an array (handles null being passed explicitly)
  const safeIdUploads = idUploads || [];

  // set_true_if_invisible (additional_props → setTrueIfInvisible):
  // Conditions hide the UI, but requirements are still assessed unless this prop is set.
  // Put it on the field that owns the hide conditions when uploads must NOT be required:
  // - Whole group optional (e.g. status = no_license / no_clearance): STI on the wrapper.
  //   Wrapper conditions are merged onto lists for visibility, but STI is NOT inherited —
  //   wrapper STI alone is enough when the whole group hides together.
  // - Individual lists optional while siblings stay required (e.g. intl per-country): STI on
  //   each conditioned list (and usually the wrapper for the all-hidden case).
  // Omit STI when a hidden field must still enforce (e.g. photo/dob/moj already-covered UI).
  const isInvisible = !isActive(wrapperField, values, fieldPathIndex);

  if (wrapperField.setTrueIfInvisible && isInvisible) {
    return {
      ...wrapperField,
      requirementsMet: true,
      verifiedRequirementsMet: true,
    };
  }

  // Without setTrueIfInvisible, assess requirements regardless of visibility
  // (including when the prop is set but the field is currently visible)
  const groupAppliedGlobal = new Set();
  const groupAppliedByList = new Map();
  const groupContributingUploads = new Set(); // Track uploads contributing to this group
  const updatedLists = [];
  let groupCount = 0;
  let groupPoints = 0;
  // Track verified counts separately for strict check type verification
  let verifiedGroupCount = 0;
  let verifiedGroupPoints = 0;

  const lists = (wrapperField?.subFields || [])
    .map(sf => (sf?.type === 'id_requirements_list' ? sf : null))
    .filter(Boolean);

  // Build a set of list codes in this group for cross-list conflict checking
  const listCodesInGroup = new Set(lists.map(list => list.code).filter(isStr));

  // A raw list can turn invisible (e.g. citizenship changing so commencement_of_identity no
  // longer applies) while an upload it physically owns is still pool-credited to another,
  // still-visible list. Once that happens there is no other page/list left where the candidate
  // could reach the real upload component — treating it as "credited elsewhere" there would
  // leave it permanently un-deletable/un-replaceable. See creditedFromAnotherList below.
  // This must span EVERY id_requirements group across every check type, not just this
  // wrapper's own lists — a document can be raw-uploaded under one check type (e.g. ACIC) and
  // pool-credited toward another, entirely separate check type's list (e.g. AU Federal Police).
  // From that second check type's own (group-local) perspective, ACIC's list code wouldn't
  // exist at all, which would look identical to "the raw list is gone" and wrongly hand
  // Delete/Replace control to a check type that isn't where the document actually lives.
  // globalActiveListCodes (computed once across all groups by calculateIdRequirements) fixes
  // that; falling back to this group's own lists only if it isn't provided.
  const activeListCodes =
    globalActiveListCodes ??
    new Set(lists.filter(list => isActive(list, values, fieldPathIndex)).map(list => list.code));

  const eligibleListsForPoolAssign = lists.filter(list => {
    if (list.setTrueIfInvisible && !isActive(list, values, fieldPathIndex)) return false;
    if (list?.idRequirements) return false;
    return true;
  });

  const duplicateIdValues = detectDuplicateIdValuesFromLists(eligibleListsForPoolAssign);

  const poolEffectiveAssignment = computePoolEffectiveAssignments(
    eligibleListsForPoolAssign,
    safeIdUploads,
  );

  const effectiveListCode = upload => {
    const uid = upload?.id;
    if (uid != null && poolEffectiveAssignment.has(uid)) {
      return poolEffectiveAssignment.get(uid);
    }
    return getRawListCode(upload);
  };

  const appliedSetForList = lc => {
    if (!groupAppliedByList.has(lc)) groupAppliedByList.set(lc, new Set());
    return groupAppliedByList.get(lc);
  };

  const recordGroupApplied = (listCode, idValue) => {
    if (duplicateIdValues.includes(idValue) && isStr(listCode)) {
      appliedSetForList(listCode).add(idValue);
    } else {
      groupAppliedGlobal.add(idValue);
    }
  };

  const isGroupOptionRecorded = (listCode, idValue) => {
    if (duplicateIdValues.includes(idValue) && isStr(listCode)) {
      return groupAppliedByList.get(listCode)?.has(idValue) ?? false;
    }
    return groupAppliedGlobal.has(idValue);
  };

  const uploadsByListCode = new Map();

  safeIdUploads.forEach(upload => {
    const uploadId = upload?.id;
    if (!uploadId) return;

    const uploadListCode = effectiveListCode(upload);
    const isAssignedToGroupList = isStr(uploadListCode) && listCodesInGroup.has(uploadListCode);

    if (isAssignedToGroupList) {
      if (!uploadsByListCode.has(uploadListCode)) {
        uploadsByListCode.set(uploadListCode, new Set());
      }
      uploadsByListCode.get(uploadListCode).add(upload);
    }
  });

  const uploadsForOptionOnList = (list, listCode, opt) =>
    safeIdUploads.filter(upload => {
      if (!isUploadReadyToCount(upload, opt)) return false;
      if (effectiveListCode(upload) !== listCode && !opt?.allowCrossListCount) return false;
      const match = findMatchingOptionForUpload(list, listCode, upload);
      return match?.opt?.value === opt.value;
    });

  const selectOptionIfPoolAssigned = (list, listCode, opt, listApplied) => {
    const idValue = opt.value ?? null;
    if (!isStr(idValue) || !isStr(listCode)) return false;

    const poolUploads = uploadsForOptionOnList(list, listCode, opt);
    if (poolUploads.length === 0) return false;
    if (isGroupOptionRecorded(listCode, idValue) && !opt?.allowCrossListCount) return false;

    listApplied.add(idValue);
    recordGroupApplied(listCode, idValue);
    poolUploads.forEach(upload => groupContributingUploads.add(upload));
    return true;
  };

  // A pool-credited upload's raw (physical) list must always be able to render the real
  // upload/confirm-details component for it — isUploadedInAnotherList would otherwise disable
  // (and hide entirely, see IdRequirementsList.jsx) the option in the very list the document
  // actually lives in, leaving the candidate with nowhere to confirm its extracted fields.
  const hasRawUploadForOption = (list, listCode, opt) =>
    safeIdUploads.some(upload => {
      if (!isAcceptedUpload(upload)) return false;
      if (getRawListCode(upload) !== listCode) return false;
      const match = findMatchingOptionForUpload(list, listCode, upload);
      return match?.opt?.value === opt.value;
    });

  const isUploadedInAnotherList = (opt, currentListCode) => {
    const idType = opt.idType ?? null;
    const countryCode = opt.countryCode ?? null;
    const idValue = opt.value ?? null;
    if (!isStr(idType) || !isStr(currentListCode)) return false;

    const isGeneric = !isStr(countryCode);

    for (const [listCode, uploads] of uploadsByListCode.entries()) {
      if (listCode === currentListCode) continue;

      const otherList = lists.find(l => l.code === listCode);
      if (!otherList) continue;

      for (const upload of uploads) {
        const match = findMatchingOptionForUpload(otherList, listCode, upload);
        if (!match) continue;

        const matchedOpt = match.opt;
        const matchedIdType = matchedOpt.idType ?? null;
        if (matchedIdType !== idType) continue;

        if (isGeneric) {
          return true;
        }

        if (matchedOpt.value === idValue) return true;

        const matchedCountry = matchedOpt.countryCode ?? null;
        if (match.matchKind === 'specific' && matchedCountry === countryCode) return true;

        // Specific option (e.g. driver_license:AU) when another list credited the same type
        // via a generic option (e.g. driver_license) — same upload, don't leave this
        // option selectable on the original list.
        if (match.matchKind === 'generic') return true;
      }
    }

    return false;
  };

  for (const list of lists) {
    // Only check visibility if setTrueIfInvisible prop is set
    if (list.setTrueIfInvisible && !isActive(list, values, fieldPathIndex)) {
      const next = { ...list };
      if (list.hasIdRequirements) {
        next.requirementsMet = true; // Requirements met because list is not applicable
        next.verifiedRequirementsMet = true;
      }
      updatedLists.push(next);
      continue;
    }
    // For all other cases, always assess requirements normally (ignore visibility completely)

    const listApplied = new Set();
    const listCode = list.code ?? null;

    // Check if any generic options exist in this list that would be selected
    const genericOptionsInList = (list.options || []).filter(opt => {
      const idType = opt.idType ?? null;
      const countryCode = opt.countryCode ?? null;
      return isStr(idType) && !isStr(countryCode); // Generic options have idType but no countryCode
    });

    // First pass: SPECIFIC options (TYPE:CC)
    const pass1 = (list.options || []).map(opt => {
      const idValue = opt.value ?? null;
      const idType = opt.idType ?? null;
      const countryCode = opt.countryCode ?? null;

      if (!isStr(idValue) || !isStr(idType)) {
        return { ...opt, selected: false, disabled: false };
      }

      const isSpecific = isStr(idType) && isStr(countryCode);
      if (!isSpecific) return { ...opt, selected: false, disabled: false };

      const selected =
        isSelectedId(selectedIds, idType, countryCode) &&
        selectOptionIfPoolAssigned(list, listCode, opt, listApplied);

      // selected can be true purely because the pool algorithm credited this list with an
      // upload whose raw (physical) list is actually somewhere else — uploadsForOptionOnList
      // matches on effectiveListCode, which follows the pool reassignment, not where the
      // document actually lives. IdRequirementsList.jsx needs this distinction to avoid
      // rendering the real upload slot (with the other list's file/verify state) here too.
      // A null raw list code (legacy upload predating this scoping, or one whose verify hasn't
      // recorded it yet) is "unknown", not "confirmed elsewhere" — treat it as belonging here
      // so a legacy upload doesn't get hidden from its own list's real upload slot.
      const creditedFromAnotherList =
        selected &&
        uploadsForOptionOnList(list, listCode, opt).every(u => {
          const raw = getRawListCode(u);
          return raw != null && raw !== listCode && activeListCodes.has(raw);
        });

      const hasGenericInList = genericOptionsInList.some(gOpt => gOpt.idType === idType);
      let genericSelectedInThisList = false;

      if (hasGenericInList && isStr(listCode)) {
        const genericOpt = genericOptionsInList.find(gOpt => gOpt.idType === idType);
        genericSelectedInThisList =
          !!genericOpt &&
          uploadsForOptionOnList(list, listCode, genericOpt).length > 0 &&
          !hasSpecificApplied(listApplied, idType);
      }

      const disabled =
        !selected &&
        !hasRawUploadForOption(list, listCode, opt) &&
        (isMutuallyExclusiveOptSelected(opt, selectedIds) ||
          genericSelectedInThisList ||
          isUploadedInAnotherList(opt, listCode));

      return { ...opt, selected, disabled, creditedFromAnotherList };
    });

    // Second pass: GENERIC options (TYPE)
    const pass2 = pass1.map(opt => {
      const idValue = opt.value ?? null;
      const idType = opt.idType ?? null;
      const countryCode = opt.countryCode ?? null;

      if (!isStr(idValue) || !isStr(idType)) {
        return { ...opt, selected: false, disabled: false };
      }

      const isGeneric = isStr(idType) && !isStr(countryCode);
      if (!isGeneric) return { ...opt };

      const listHasSpecificByType = hasSpecificApplied(listApplied, idType);
      const selected =
        isSelectedId(selectedIds, idType) &&
        !listHasSpecificByType &&
        selectOptionIfPoolAssigned(list, listCode, opt, listApplied);

      // See the identical check in pass1 above — selected here can also be purely a pool
      // credit from another list's upload.
      // A null raw list code (legacy upload predating this scoping, or one whose verify hasn't
      // recorded it yet) is "unknown", not "confirmed elsewhere" — treat it as belonging here
      // so a legacy upload doesn't get hidden from its own list's real upload slot.
      const creditedFromAnotherList =
        selected &&
        uploadsForOptionOnList(list, listCode, opt).every(u => {
          const raw = getRawListCode(u);
          return raw != null && raw !== listCode && activeListCodes.has(raw);
        });

      const disabled =
        !selected &&
        !hasRawUploadForOption(list, listCode, opt) &&
        (isMutuallyExclusiveOptSelected(opt, selectedIds) ||
          listHasSpecificByType ||
          (isGroupOptionRecorded(listCode, idValue) && !opt?.allowCrossListCount) ||
          isUploadedInAnotherList(opt, listCode));

      return { ...opt, selected, disabled, creditedFromAnotherList };
    });

    let count = 0;
    let points = 0;
    // Track verified counts separately for strict check type verification
    let verifiedCount = 0;
    let verifiedPoints = 0;

    pass2.forEach(o => {
      const compVal = isStr(o?.complementary) ? o.complementary : '';
      const hasComp = compVal.length > 0;

      if (hasComp) {
        const { idType: compIdType, countryCode: compCountryCode } = splitIdValue(compVal);

        o.needsComplementaryDoc =
          o.selected && !isSelectedId(selectedIds, compIdType, compCountryCode);
      } else {
        // prevent stale UI flags from previous runs
        if ('needsComplementaryDoc' in o) delete o.needsComplementaryDoc;
      }

      // don't count if not selected, or if complementary is required but not met
      if (!o.selected) return;
      if (hasComp && o.needsComplementaryDoc) return;

      // Mutually exclusive - if the mutually exclusive ids are selected through other lists they will still add to count
      // Not an issue at the moment, but may need to adjust count in the future
      const p = Number.isFinite(o.points) ? o.points : 1;
      const cv = Number.isFinite(o.countValue) ? o.countValue : 1;
      count += cv;
      points += p;
      groupCount += cv;
      groupPoints += p;

      // Check if this option has at least one verified upload for strict verification
      // Also counts not_applicable (skip_verification) uploads as passing
      const poolUploads = uploadsForOptionOnList(list, listCode, o);
      const hasVerifiedUpload = poolUploads.some(upload => isVerifiedUpload(upload));

      if (hasVerifiedUpload) {
        verifiedCount += cv;
        verifiedPoints += p;
        verifiedGroupCount += cv;
        verifiedGroupPoints += p;
      }
    });

    // Also count actual uploads assigned to this list to ensure min requirement is met
    // This is important for non-ID documents that may not have selected options but have uploads
    const uploadsAssignedToList = safeIdUploads.filter(upload => {
      if (effectiveListCode(upload) !== listCode) return false;
      const match = findMatchingOptionForUpload(list, listCode, upload);
      if (!match) return false;
      return isUploadReadyToCount(upload, match.opt);
    });

    // Use the maximum of selected options count and actual uploads count
    // This ensures we count uploads even if options aren't selected (e.g., for non-ID documents)
    const actualUploadCount = uploadsAssignedToList.length;
    const effectiveCount = Math.max(count, actualUploadCount);

    // Calculate verified upload count for strict verification
    // Also counts not_applicable (skip_verification) uploads as passing
    const verifiedUploadsAssignedToList = uploadsAssignedToList.filter(upload =>
      isVerifiedUpload(upload),
    );
    const verifiedActualUploadCount = verifiedUploadsAssignedToList.length;
    const verifiedEffectiveCount = Math.max(verifiedCount, verifiedActualUploadCount);

    const needCount = numOrNull(list?.min || null);
    const needPoints = numOrNull(list?.minPoints || null);
    const meetsCount = needCount == null ? true : effectiveCount >= needCount;
    const meetsPoints = needPoints == null ? true : points >= needPoints;
    // Strict verification: check if requirements met using only verified uploads
    const verifiedMeetsCount = needCount == null ? true : verifiedEffectiveCount >= needCount;
    const verifiedMeetsPoints = needPoints == null ? true : verifiedPoints >= needPoints;

    const nextList = { ...list, options: pass2 };
    if (list.hasIdRequirements) {
      // Where alternative idRequirements conditions are defined, they determine
      // requirementsMet directly instead of the min/points count above — but pass1/pass2 still
      // ran so options[].selected (and therefore the "X uploaded" display) reflects reality,
      // and any matching upload is still tracked as contributing via the normal pool-assign path.
      if (list?.idRequirements) {
        let met = false;
        let verifiedMet = false;
        try {
          met = !!evaluateFieldConditions(list.idRequirements, values, { fieldPathIndex });
          verifiedMet = !!evaluateFieldConditions(list.idRequirements, verifiedValues, {
            fieldPathIndex,
          });
        } catch {}
        nextList.requirementsMet = met;
        nextList.verifiedRequirementsMet = verifiedMet;
      } else {
        nextList.requirementsMet = meetsCount && meetsPoints;
        nextList.verifiedRequirementsMet = verifiedMeetsCount && verifiedMeetsPoints;
      }
    }
    updatedLists.push(nextList);
  }

  // Wrapper requirements
  const wrapperMin = numOrNull(wrapperField?.min);
  const wrapperMinPoints = numOrNull(wrapperField?.minPoints);

  // Only consider lists that are active (hidden ones were pushed as met=true already)
  const allListsRequirementsMet = updatedLists.every(
    l => !l.hasIdRequirements || l.requirementsMet === true,
  );
  // Check verified requirements for strict check type verification
  const allListsVerifiedRequirementsMet = updatedLists.every(
    l => !l.hasIdRequirements || l.verifiedRequirementsMet === true,
  );

  let wrapperRequirementsMet = allListsRequirementsMet;
  let wrapperVerifiedRequirementsMet = allListsVerifiedRequirementsMet;

  if (wrapperMin != null || wrapperMinPoints != null) {
    const meetsGroupCount = wrapperMin == null ? true : groupCount >= wrapperMin;
    const meetsGroupPoints = wrapperMinPoints == null ? true : groupPoints >= wrapperMinPoints;
    wrapperRequirementsMet = allListsRequirementsMet && meetsGroupCount && meetsGroupPoints;

    // Strict verification uses verified counts
    const verifiedMeetsGroupCount = wrapperMin == null ? true : verifiedGroupCount >= wrapperMin;
    const verifiedMeetsGroupPoints =
      wrapperMinPoints == null ? true : verifiedGroupPoints >= wrapperMinPoints;
    wrapperVerifiedRequirementsMet =
      allListsVerifiedRequirementsMet && verifiedMeetsGroupCount && verifiedMeetsGroupPoints;
  } else {
    // If no min/minPoints on wrapper, just check if all lists meet their requirements
    wrapperRequirementsMet = allListsRequirementsMet;
    wrapperVerifiedRequirementsMet = allListsVerifiedRequirementsMet;
  }

  const nextWrapper = {
    ...wrapperField,
    subFields: updatedLists,
  };

  // only set on wrapper if backend had it OR wrapper has its own min/points OR any child carries the flag
  if (
    wrapperField.hasIdRequirements ||
    wrapperMin != null ||
    wrapperMinPoints != null ||
    updatedLists.some(l => l.hasIdRequirements)
  ) {
    nextWrapper.requirementsMet = wrapperRequirementsMet;
    nextWrapper.verifiedRequirementsMet = wrapperVerifiedRequirementsMet;
  }

  // Attach contributing uploads to the wrapper for tracking
  nextWrapper.contributingUploads = Array.from(groupContributingUploads);

  return nextWrapper;
};

// --- public: walk all sections and update only id_requirements fields ---
const collectOptions = sections => {
  const options = [];
  (sections || []).forEach(section => {
    (section?.subsections || []).forEach(sub => {
      (sub?.fields || []).forEach(field => {
        if (field?.type !== 'id_requirements') return;
        (field?.subFields || []).forEach(list => {
          (list?.options || []).forEach(opt => options.push({ ...opt, listCode: list.code }));
        });
      });
    });
  });
  return options;
};

// A front without its required back must not feed hidden values (photo/dob ID flags) or
// selected ids, otherwise lists driven by idRequirements conditions are met by the front alone.
const hasMissingRequiredBack = (upload, options) => {
  if (upload?.metadata?.dual === 'back') return false;
  const { idType, countryCode } = splitIdValue(upload?.metadata?.id_type);
  if (!isStr(idType)) return false;
  const rawListCode = getRawListCode(upload);
  const candidateOptions = isStr(rawListCode)
    ? options.filter(opt => opt.listCode === rawListCode)
    : options;
  const matching = candidateOptions.filter(
    opt =>
      opt?.value === upload.metadata.id_type ||
      (opt?.idType === idType && !isStr(opt?.countryCode) && isStr(countryCode)) ||
      (opt?.idType === idType && !isStr(countryCode) && !isStr(opt?.countryCode)),
  );
  if (matching.length === 0) return false;
  return matching.every(opt => opt.dual === true && !isDualOptionSatisfied(upload, opt));
};

export const calculateIdRequirements = (
  sections = [],
  idUploads = [],
  values = {},
  fieldPathIndex = {},
  currentIdRequirementsMet = {},
  currentSelectedIdDocuments = {},
  hiddenFields = {},
  hiddenValues = {},
  hiddenDependencyCodes = {},
  checkTypeIdRequirements = {}, // Map of check_type_slug -> { requirements: [], requirementsMet: boolean }
  dynamicDependencyCodes = null,
  templateSources = {},
) => {
  // Ensure idUploads is always an array (handles null being passed explicitly)
  const dualOptions = collectOptions(sections);
  const safeIdUploads = (idUploads || []).filter(
    upload => !hasMissingRequiredBack(upload, dualOptions),
  );

  const {
    selectedIdTypes,
    verifiedSelectedIdTypes,
    validUploads,
    hasDobId,
    hasPhotoId,
    verifiedHasDobId,
    verifiedHasPhotoId,
  } = computeSelectedIdsFromUploads(safeIdUploads);

  // Create values object with filtered uploads and selected types for hidden processors
  const valuesWithUploads = {
    ...values,
    validUploads,
    selectedIdTypes,
    hasDobId,
    hasPhotoId,
  };

  // Strict check-type verification uses verified and not_applicable uploads only.
  const verifiedUploads = validUploads.filter(u => isVerifiedUpload(u));
  const verifiedValuesWithUploads = {
    ...values,
    validUploads: verifiedUploads,
    selectedIdTypes: verifiedSelectedIdTypes,
    hasDobId: verifiedHasDobId,
    hasPhotoId: verifiedHasPhotoId,
  };

  // Run hidden field processors that depend on __id_uploads__
  const idUploadDependents = hiddenDependencyCodes['__id_uploads__'] || [];

  let updatedHiddenValues = { ...hiddenValues };
  let verifiedHiddenValues = { ...hiddenValues };
  let uploadHiddenUpdates = null;

  if (idUploadDependents.length > 0) {
    const hiddenUpdates = runHiddenDependentsChained(
      idUploadDependents,
      valuesWithUploads,
      hiddenFields,
      hiddenDependencyCodes,
    );
    if (hiddenUpdates && Object.keys(hiddenUpdates).length > 0) {
      uploadHiddenUpdates = hiddenUpdates;
      updatedHiddenValues = {
        ...updatedHiddenValues,
        ...hiddenUpdates,
      };
    }

    const verifiedHiddenUpdates = runHiddenDependentsChained(
      idUploadDependents,
      verifiedValuesWithUploads,
      hiddenFields,
      hiddenDependencyCodes,
    );
    if (verifiedHiddenUpdates && Object.keys(verifiedHiddenUpdates).length > 0) {
      verifiedHiddenValues = {
        ...verifiedHiddenValues,
        ...verifiedHiddenUpdates,
      };
    }
  }

  // Create final values object with updated hidden values
  const allValues = {
    ...values,
    ...updatedHiddenValues,
  };

  // Create verified values for strict check type verification
  const verifiedAllValues = {
    ...values,
    ...verifiedHiddenValues,
  };

  // Initialize tracking objects
  const idRequirementsMet = { ...currentIdRequirementsMet };
  const verifiedIdRequirementsMet = {}; // Track verified requirements met for strict check type verification
  const selectedIdDocuments = { ...currentSelectedIdDocuments };
  const requirementContributingUploads = {}; // Track uploads contributing to each requirement field code
  const updatedCheckTypeIdRequirements = { ...checkTypeIdRequirements };

  // Initialize Sets to track contributing uploads for each check type
  const checkTypeContributingUploads = {};
  for (const [checkTypeSlug, checkTypeData] of Object.entries(updatedCheckTypeIdRequirements)) {
    if (checkTypeData && typeof checkTypeData === 'object') {
      checkTypeContributingUploads[checkTypeSlug] = new Set();
    }
  }

  let sectionsForIdReq = sections;
  if (dynamicDependencyCodes && uploadHiddenUpdates) {
    const dynamicSections = runDynamicDependentsForHiddenUpdates(
      uploadHiddenUpdates,
      sections,
      fieldPathIndex,
      allValues,
      dynamicDependencyCodes,
      templateSources,
    );
    if (dynamicSections) {
      sectionsForIdReq = dynamicSections;
    }
  }

  // Active list codes across EVERY id_requirements group in every check type (not just one
  // group at a time) — see updateIdRequirementsGroup's activeListCodes for why a document
  // credited across check-type boundaries needs this to recognize its true home is still
  // reachable, rather than wrongly treating it as gone.
  const globalActiveListCodes = new Set();
  (sectionsForIdReq || []).forEach(section => {
    (section?.subsections || []).forEach(sub => {
      (sub?.fields || []).forEach(field => {
        if (field?.type !== 'id_requirements') return;
        (field?.subFields || []).forEach(list => {
          if (
            list?.type === 'id_requirements_list' &&
            isStr(list.code) &&
            isActive(list, allValues, fieldPathIndex)
          ) {
            globalActiveListCodes.add(list.code);
          }
        });
      });
    });
  });

  const updatedSections = (sectionsForIdReq || []).map(section => ({
    ...section,
    subsections: (section?.subsections || []).map(sub => ({
      ...sub,
      fields: (sub?.fields || []).map(field => {
        if (field?.type === 'id_requirements') {
          const updatedField = updateIdRequirementsGroup(
            field,
            selectedIdTypes,
            allValues,
            fieldPathIndex,
            validUploads,
            verifiedAllValues,
            globalActiveListCodes,
          );

          // Collect requirements met status during processing
          // Only add to idRequirementsMet if the field actually has requirements
          if (field.code && updatedField.hasIdRequirements) {
            idRequirementsMet[field.code] = updatedField.requirementsMet || false;
            verifiedIdRequirementsMet[field.code] = updatedField.verifiedRequirementsMet || false;

            // Track contributing uploads for this requirement field code
            if (Array.isArray(updatedField.contributingUploads)) {
              requirementContributingUploads[field.code] = updatedField.contributingUploads;

              // If this field code is in any check type's requirements, add uploads to that check type's Set
              for (const [checkTypeSlug, checkTypeData] of Object.entries(
                updatedCheckTypeIdRequirements,
              )) {
                if (checkTypeData && typeof checkTypeData === 'object') {
                  const requirements = Array.isArray(checkTypeData.requirements)
                    ? checkTypeData.requirements
                    : [];
                  if (requirements.includes(field.code)) {
                    // Add all contributing uploads to this check type's Set
                    updatedField.contributingUploads.forEach(upload => {
                      checkTypeContributingUploads[checkTypeSlug]?.add(upload);
                    });
                  }
                }
              }
            }
          }

          // Collect selected documents during processing
          if (updatedField.subFields) {
            updatedField.subFields.forEach(subField => {
              if (subField.type === 'id_requirements_list' && subField.code) {
                const selectedOptions = (subField.options || [])
                  .filter(option => option.selected)
                  .map(option => option.value);
                selectedIdDocuments[subField.code] = selectedOptions;
              }
            });
          }

          return updatedField;
        }
        return field;
      }),
    })),
  }));

  // Calculate checkTypeIdRequirements.requirementsMet using verifiedIdRequirementsMet
  // This ensures check types use the strict verification (verified uploads only)
  for (const [checkTypeSlug, checkTypeData] of Object.entries(updatedCheckTypeIdRequirements)) {
    if (!checkTypeData || typeof checkTypeData !== 'object') continue;

    const requirements = Array.isArray(checkTypeData.requirements)
      ? checkTypeData.requirements
      : [];

    // No requirements = requirements met by default
    // Otherwise, check all requirements have verifiedRequirementsMet = true
    const requirementsMet =
      requirements.length === 0
        ? true
        : requirements.every(reqCode => verifiedIdRequirementsMet[reqCode] === true);

    updatedCheckTypeIdRequirements[checkTypeSlug] = {
      requirements,
      requirementsMet,
    };
  }

  return {
    updatedSections,
    updatedHiddenValues,
    idRequirementsMet,
    selectedIdDocuments,
    checkTypeIdRequirements: updatedCheckTypeIdRequirements,
  };
};
